import { useEffect, useRef } from 'react';

export type ConfirmDialogVariant = 'default' | 'warning' | 'danger';

export type ConfirmField = {
  key: string;
  label: string;
  type?: 'text' | 'number' | 'textarea' | 'select';
  placeholder?: string;
  defaultValue?: string | number;
  required?: boolean;
  /** Options for type: 'select' — array of [value, label] tuples or strings */
  options?: Array<string | { value: string; label: string }>;
};

type Props = {
  open: boolean;
  title: string;
  description?: string;
  variant?: ConfirmDialogVariant;
  confirmLabel?: string;
  loading?: boolean;
  fields?: ConfirmField[];
  fieldValues?: Record<string, string>;
  onFieldChange?: (key: string, value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
};

export default function ConfirmDialog({
  open,
  title,
  description,
  variant = 'default',
  confirmLabel = 'Confirm',
  loading = false,
  fields = [],
  fieldValues = {},
  onFieldChange,
  onConfirm,
  onCancel,
}: Props) {
  const overlayRef = useRef<HTMLDivElement>(null);

  // Close on overlay click
  function handleOverlayClick(e: React.MouseEvent) {
    if (e.target === overlayRef.current) {
      onCancel();
    }
  }

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const confirmBtnClass =
    variant === 'danger'  ? 'btn btn-danger' :
    variant === 'warning' ? 'btn btn-warning' :
                            'btn btn-primary';

  return (
    <div className="modal-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="dialog-title">
        <div className="modal-title" id="dialog-title">{title}</div>
        {description && (
          <div className="modal-description">{description}</div>
        )}

        {fields.map((f) => (
          <div className="form-group" key={f.key}>
            <label htmlFor={`dialog-field-${f.key}`}>{f.label}</label>
            {f.type === 'textarea' ? (
              <textarea
                id={`dialog-field-${f.key}`}
                placeholder={f.placeholder}
                value={fieldValues[f.key] ?? String(f.defaultValue ?? '')}
                onChange={(e) => onFieldChange?.(f.key, e.target.value)}
              />
            ) : f.type === 'select' ? (
              <select
                id={`dialog-field-${f.key}`}
                value={fieldValues[f.key] ?? String(f.defaultValue ?? '')}
                onChange={(e) => onFieldChange?.(f.key, e.target.value)}
              >
                {(f.options ?? []).map((opt) => {
                  const value = typeof opt === 'string' ? opt : opt.value;
                  const label = typeof opt === 'string' ? opt : opt.label;
                  return <option key={value} value={value}>{label}</option>;
                })}
              </select>
            ) : (
              <input
                id={`dialog-field-${f.key}`}
                type={f.type ?? 'text'}
                placeholder={f.placeholder}
                value={fieldValues[f.key] ?? String(f.defaultValue ?? '')}
                onChange={(e) => onFieldChange?.(f.key, e.target.value)}
              />
            )}
          </div>
        ))}

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
          <button className={confirmBtnClass} onClick={onConfirm} disabled={loading}>
            {loading ? <span className="spinner" /> : null}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
